import React, { useEffect, useState } from 'react';
import { publicApi } from '../utils/api';

/**
 * PublicActivityFeed — social proof live pe homepage.
 *
 * Arata ultimele scanari de pe platforma (anonimizate: doar brand + scor + timp).
 * Refresh la 60 sec ca sa vada vizitatorii ca platforma e activa.
 *
 * Endpoint: GET /api/public/activity-feed (cache 60 sec server-side).
 */
export default function PublicActivityFeed({ limit = 6 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetchFeed = () => {
      publicApi.activityFeed?.(limit)
        .then(res => {
          if (alive) {
            setItems(res.data?.items || []);
            setLoading(false);
          }
        })
        .catch(() => { if (alive) setLoading(false); });
    };
    fetchFeed();
    const id = setInterval(fetchFeed, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, [limit]);

  const relTime = (iso) => {
    if (!iso) return '';
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60_000);
    if (min < 1) return 'acum';
    if (min < 60) return `${min}m`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    return `${d}z`;
  };

  // Unified thresholds (80/60/40) — aligned with SCORE_TIERS
  const scoreColor = (s) => s >= 80 ? '#2d6a4f' : s >= 60 ? '#D4AF37' : s >= 40 ? '#e67e22' : '#c0392b';

  if (loading || items.length === 0) return null;

  return (
    <div style={{
      background: '#FFFFFF',
      border: '1px solid #E5E7EB',
      borderRadius: 16,
      padding: 'clamp(20px, 3vw, 28px)',
      maxWidth: 720,
      margin: '0 auto',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 16,
        fontSize: 10,
        color: '#6B7280',
        fontFamily: 'DM Mono, monospace',
        letterSpacing: 2,
        textTransform: 'uppercase',
        fontWeight: 700,
      }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#10B981', boxShadow: '0 0 0 4px rgba(16,185,129,0.15)' }} />
        Activitate live
      </div>
      <div>
        {items.map((it, i) => (
          <div key={i} style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto',
            gap: 16,
            alignItems: 'center',
            padding: '10px 0',
            borderBottom: i < items.length - 1 ? '1px solid #F3F4F6' : 'none',
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 15, fontWeight: 700, color: '#0A1428', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {it.brand_name}
              </div>
              <div style={{ fontSize: 11, color: '#6B7280', fontFamily: 'DM Mono, monospace', letterSpacing: 1, textTransform: 'uppercase' }}>
                {it.industry_label}
              </div>
            </div>
            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 18, fontWeight: 700, color: scoreColor(it.score) }}>
              {it.score}
            </div>
            <div style={{ fontSize: 11, color: '#6B7280', fontFamily: 'DM Mono, monospace' }}>
              {relTime(it.calculated_at)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
